import { PickerChangeHandlerContext } from "@mui/x-date-pickers";
import { ButtonComponent, DatepickerComponent } from "@zenith/components";
import { memo, useState } from "react";

/* eslint-disable-next-line */
export interface DateTransactionsFilterProps {
  minDate: unknown | undefined;
  maxDate: unknown | undefined;
  clear: () => void;
  apply: (startDate: unknown | undefined, endDate: unknown | undefined) => void;
  /** When true, a start date must be picked before the filter can be applied. */
  requireStartDate?: boolean;
}

const DateTransactionsFilter = memo(function DateTransactionsFilter(
  props: DateTransactionsFilterProps
) {
  const [startDate, setStartDate] = useState<unknown | undefined>(
    props.minDate
  );
  const [endDate, setEndDate] = useState<unknown | undefined>(props.maxDate);
  const [initialCalendarOpen, setInitialCalendarOpen] =
    useState<boolean>(false);
  const [endCalendarOpen, setEndCalendarOpen] = useState<boolean>(false);
  const [isStartDateTouched, setIsStartDateTouched] = useState<boolean>(false);

  const showStartDateRequired =
    !!props.requireStartDate && !startDate && (isStartDateTouched || !!endDate);
  const isApplyDisabled = props.requireStartDate
    ? !startDate
    : !startDate && !endDate;

  function setInitialDateFormated(
    date: unknown,
    context: PickerChangeHandlerContext<unknown>
  ) {
    setInitialCalendarOpen(false);
    if (date && !context.validationError) {
      setStartDate(date);
    }
  }

  function setEndDateFormated(
    date: unknown,
    context: PickerChangeHandlerContext<unknown>
  ) {
    setEndCalendarOpen(false);
    if (date && !context.validationError) {
      setEndDate(date);
    }
  }

  function clearFilters() {
    setStartDate(undefined);
    setEndDate(undefined);
    setIsStartDateTouched(false);
  }

  return (
    <div className="w-filter-modal py-2">
      <div className="mb-4 px-2">
        <DatepickerComponent
          disableFuture
          id="start-date-filter"
          maxDate={endDate || undefined}
          labelId="start-date-filter-label"
          className="[&>label]:text-main-200-50 w-full"
          label="Start date"
          format="DD/MM/YYYY"
          formatDensity="spacious"
          height="40px"
          showCalendarIcon={false}
          value={startDate || null}
          isOpen={initialCalendarOpen}
          onClose={() => {
            setInitialCalendarOpen(false);
            setIsStartDateTouched(true);
          }}
          onChange={(
            value: unknown,
            context: PickerChangeHandlerContext<unknown>
          ) => setInitialDateFormated(value, context)}
          validationMessage={"Start date is required"}
          hasError={showStartDateRequired}
          slotProps={{
            field: {
              clearable: true,
              onClear: (event) => {
                setStartDate(undefined);
                event.stopPropagation();
              },
              onKeyDown: (event) => event.stopPropagation(),
            },
            textField: {
              placeholder: "Select a date",
              type: "text",
              onKeyDown: (event) => event.stopPropagation(),
              onClick: () => setInitialCalendarOpen(true),
              focused: false,
            },
          }}
        />
      </div>
      <div className="mb-8 px-2">
        <DatepickerComponent
          disableFuture
          id="end-date-filter"
          minDate={startDate || undefined}
          labelId="end-date-filter-label"
          className="[&>label]:text-main-200-50 w-full"
          label="End date"
          format="DD/MM/YYYY"
          formatDensity="spacious"
          height="40px"
          showCalendarIcon={false}
          value={endDate || null}
          isOpen={endCalendarOpen}
          onChange={(
            value: unknown,
            context: PickerChangeHandlerContext<unknown>
          ) => setEndDateFormated(value, context)}
          validationMessage={""}
          hasError={false}
          slotProps={{
            field: {
              clearable: true,
              onClear: (event) => {
                setEndDate(undefined);
                event.stopPropagation();
              },
            },
            textField: {
              placeholder: "Select a date",
              onKeyDown: (event) => event.stopPropagation(),
              onClick: () => setEndCalendarOpen(true),
              focused: false,
            },
          }}
        />
      </div>
      <div className="flex w-full flex-wrap justify-between px-2 visited:bg-transparent hover:bg-transparent">
        <div className="my-auto w-1/2">
          <ButtonComponent
            label="Clear all"
            onClick={() => {
              clearFilters();
              props.clear();
            }}
            id="clear-all-button"
            fullWidth
            size="medium"
            type="tertiary"
            disabled={!startDate && !endDate}
          />
        </div>
        <div className="w-1/2">
          <ButtonComponent
            label="Apply"
            onClick={() => {
              props.apply(startDate, endDate);
            }}
            id="apply-button"
            fullWidth
            size="medium"
            type="primary"
            disabled={isApplyDisabled}
          />
        </div>
      </div>
    </div>
  );
});

export default DateTransactionsFilter;
